import { Tooltip } from "@shared/components/untitledui/base/tooltip/tooltip"
import { cx } from "@shared/components/untitledui/util/cx"
import type { ReactNode } from "react"
import { ToggleButton as AriaToggleButton } from "react-aria-components"
import { TOOLBAR_BUTTON_CHROME, TOOLBAR_TOGGLE_SELECTED } from "./toolbar-button-chrome"

/**
 * The toolbar's real toggle control: a react-aria `ToggleButton`, so an active
 * format reaches assistive tech as `aria-pressed` (not just a className). The
 * pressed-in look is driven off `data-selected` via `TOOLBAR_TOGGLE_SELECTED`.
 *
 * The split color/highlight/underline controls don't use this — their own icon
 * carries the color, so they build their `AriaToggleButton` directly without the
 * neutral fill (see `ColorSplitControl`).
 */
export function ToolbarToggleButton({
  isSelected,
  onToggle,
  isDisabled,
  ariaLabel,
  tooltip,
  className,
  children,
}: {
  isSelected: boolean
  onToggle: () => void
  isDisabled?: boolean
  ariaLabel: string
  /** Tooltip content; defaults to `ariaLabel`. */
  tooltip?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <Tooltip title={tooltip ?? ariaLabel} closeDelay={0}>
      <AriaToggleButton
        isSelected={isSelected}
        onChange={onToggle}
        isDisabled={isDisabled}
        aria-label={ariaLabel}
        className={cx(
          "flex cursor-pointer items-center outline-brand transition",
          "focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          TOOLBAR_BUTTON_CHROME,
          TOOLBAR_TOGGLE_SELECTED,
          className,
        )}
      >
        {children}
      </AriaToggleButton>
    </Tooltip>
  )
}
